import { BreakpointObserver } from '@angular/cdk/layout';
import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { OrderDetailModalComponent } from '../order-detail-modal/order-detail-modal.component';
import { DocumentGenerateService } from '../../document-generate.service';
import { DocumentGenerateConfigService } from '../../document-generate-config.service';
import { DgNumberPipe } from '../../dg-number.pipe';
import { LanguageService } from 'app/core/i18n/language.service';
import { TranslatePipe } from 'app/core/i18n/translate.pipe';

@Component({
  selector: 'app-my-daily-transactions-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, MatDialogModule, MatButtonModule, MatIconModule, MatTooltipModule, DgNumberPipe, TranslatePipe],
  // el host llena la altura fija del diálogo (dialogSize) para que el footer quede pegado y el listado tenga scroll interno
  host: { class: 'flex h-full min-h-0 w-full flex-col' },
  template: `
    <!-- header -->
    <div class="flex shrink-0 items-start justify-between bg-primary-50 px-6 dark:bg-primary-900/10"
      [ngClass]="shortScreen ? 'py-2' : 'py-4'">
      <div class="flex items-center gap-2">
        <mat-icon class="text-primary" fontSet="material-icons-outlined">calendar_today</mat-icon>
        <h2 class="text-xl font-bold">{{ 'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.TITLE' | t }}</h2>
      </div>
      <button mat-icon-button type="button" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <!-- búsqueda (filtrado en backend) -->
    <div class="shrink-0 border-b px-6" [ngClass]="shortScreen ? 'py-2' : 'py-3'">
      <div class="relative">
        <mat-icon class="text-secondary pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-lg" fontSet="material-icons-outlined">search</mat-icon>
        <input
          type="text"
          [ngModel]="search"
          (ngModelChange)="onSearchChange($event)"
          [placeholder]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.SEARCH_PLACEHOLDER' | t"
          class="w-full rounded-lg border bg-card py-2 pl-9 pr-9 text-sm outline-none focus:border-primary" />
        @if (search) {
        <button type="button" class="absolute right-1 top-1/2 -translate-y-1/2" [matTooltip]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.CLEAR' | t" (click)="onSearchChange('')">
          <mat-icon class="text-secondary text-lg" fontSet="material-icons-outlined">close</mat-icon>
        </button>
        }
      </div>
    </div>

    <!-- body (altura fija, scroll interno) -->
    <div class="min-h-0 flex-1 overflow-hidden">
      @if (loading) {
      <div class="text-secondary flex h-full items-center justify-center text-sm">{{ 'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.LOADING' | t }}</div>
      } @else if (orders.length) {

      <!-- móvil: tarjetas -->
      <div class="flex h-full flex-col overflow-auto p-4 md:hidden">
        <div class="flex flex-col gap-3">
          @for (order of orders; track order.id) {
          <div class="flex flex-col rounded-xl border bg-card p-3">
            <div class="flex items-start justify-between gap-2">
              <div class="flex min-w-0 flex-col">
                <span class="text-primary font-semibold">{{ order.clientId }}</span>
                <span class="text-secondary text-sm uppercase">{{ order.clientName }}</span>
              </div>
              <div class="flex shrink-0 items-center gap-1">
                <button mat-icon-button color="primary" type="button" [matTooltip]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.VIEW_DOCUMENT' | t" (click)="onView(order)">
                  <mat-icon>visibility</mat-icon>
                </button>
                <button mat-icon-button color="primary" type="button" [matTooltip]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.PRINT_DOCUMENT' | t" (click)="onPrint(order)">
                  <mat-icon>print</mat-icon>
                </button>
              </div>
            </div>

            <div class="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 border-t pt-3 text-sm">
              <div class="col-span-2 flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.DOCUMENT_CLASS' | t }}</span>
                <span><span class="text-primary font-semibold">{{ order.classCode || '-' }}</span> - {{ order.className }}</span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.DOCUMENT_NUMBER' | t }}</span>
                <span class="text-primary font-semibold">#{{ order.serial }}</span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.ERP_NUMBER' | t }}</span>
                <span>{{ order.erpCode || '-' }}</span>
              </div>
              <div class="col-span-2 flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.SALES_OFFICE' | t }}</span>
                <span><span class="text-primary font-semibold">{{ order.salesOfficeCode || '-' }}</span> - {{ order.salesOffice }}</span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.ITEMS' | t }}</span>
                <span>{{ order.products?.length || 0 }}</span>
              </div>
              <div class="flex flex-col gap-0.5">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.DATE' | t }}</span>
                <span>{{ order.date | date:'dd/MM/yyyy' }}</span>
              </div>
              <div class="col-span-2 flex flex-col items-end gap-0.5 border-t pt-2 text-right">
                <span class="text-secondary text-xs font-medium uppercase">{{ 'DOCUMENT_GENERATE.FIELDS.GRAND_TOTAL' | t }}</span>
                <span class="text-primary font-semibold">{{ order.currency }}&nbsp;{{ order.totalAmount | dgNumber }}</span>
              </div>
            </div>
          </div>
          }
        </div>
      </div>

      <!-- desktop/tablet: tabla -->
      <div class="hidden h-full overflow-auto md:block">
        <table class="w-full min-w-[920px] border-collapse text-base">
          <thead class="sticky top-0 z-10 bg-gray-50 dark:bg-gray-800">
            <tr class="text-secondary text-left">
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.CLIENT' | t }}</th>
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.DOCUMENT_CLASS' | t }}</th>
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.DOCUMENT_NUMBER' | t }}</th>
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.ERP_NUMBER' | t }}</th>
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.SALES_OFFICE' | t }}</th>
              <th class="px-4 py-3 text-center font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.ITEMS' | t }}</th>
              <th class="px-4 py-3 font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.DATE' | t }}</th>
              <th class="px-4 py-3 text-right font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.GRAND_TOTAL' | t }}</th>
              <th class="px-4 py-3 text-center font-medium">{{ 'DOCUMENT_GENERATE.COLUMNS.ACTIONS' | t }}</th>
            </tr>
          </thead>
          <tbody>
            @for (order of orders; track order.id) {
            <tr class="border-t">
              <td class="px-4 py-3">
                <div class="flex flex-col">
                  <span class="text-primary font-semibold">{{ order.clientId }}</span>
                  <span class="uppercase">{{ order.clientName }}</span>
                </div>
              </td>
              <td class="px-4 py-3">
                <span class="text-primary font-semibold">{{ order.classCode || '-' }}</span> - {{ order.className }}
              </td>
              <td class="px-4 py-3">
                <span class="text-primary font-semibold">#{{ order.serial }}</span>
              </td>
              <td class="px-4 py-3">{{ order.erpCode || '-' }}</td>
              <td class="px-4 py-3">
                <span class="text-primary font-semibold">{{ order.salesOfficeCode || '-' }}</span> - {{ order.salesOffice }}
              </td>
              <td class="px-4 py-3 text-center">{{ order.products?.length || 0 }}</td>
              <td class="px-4 py-3">{{ order.date | date:'dd/MM/yyyy' }}</td>
              <td class="px-4 py-3 text-right font-semibold">{{ order.currency }}&nbsp;{{ order.totalAmount | dgNumber }}</td>
              <td class="px-4 py-3">
                <div class="flex items-center justify-center gap-1">
                  <button mat-icon-button color="primary" type="button" [matTooltip]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.VIEW_DOCUMENT' | t" (click)="onView(order)">
                    <mat-icon>visibility</mat-icon>
                  </button>
                  <button mat-icon-button color="primary" type="button" [matTooltip]="'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.PRINT_DOCUMENT' | t" (click)="onPrint(order)">
                    <mat-icon>print</mat-icon>
                  </button>
                </div>
              </td>
            </tr>
            }
          </tbody>
        </table>
      </div>

      } @else {
      <div class="text-secondary flex h-full items-center justify-center text-center text-sm">
        {{ (search ? 'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.EMPTY_SEARCH' : 'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.EMPTY') | t }}
      </div>
      }
    </div>

    <!-- footer / paginación (pegado al borde) -->
    <div class="flex shrink-0 items-center justify-end gap-1 border-t bg-gray-50 px-6 dark:bg-transparent"
      [ngClass]="shortScreen ? 'py-1' : 'py-3'">
      <button mat-icon-button type="button" [disabled]="page <= 1 || loading" (click)="changePage(-1)">
        <mat-icon>chevron_left</mat-icon>
      </button>
      <span class="text-secondary text-xs">{{ 'DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.PAGE' | t: { page: page } }}</span>
      <button mat-icon-button type="button" [disabled]="!hasNext || loading" (click)="changePage(1)">
        <mat-icon>chevron_right</mat-icon>
      </button>
    </div>
  `,
})
export class MyDailyTransactionsModalComponent implements OnInit, OnDestroy {
  orders: any[] = [];
  loading = false;
  page = 1;
  hasNext = false;
  search = '';

  // en pantallas de poca altura (móvil horizontal) el encabezado no deja ver las transacciones
  shortScreen = false;
  private shortScreenSub?: Subscription;

  private searchSubject = new Subject<string>();

  constructor(
    private dialogRef: MatDialogRef<MyDailyTransactionsModalComponent>,
    private dialog: MatDialog,
    private documentGenerateService: DocumentGenerateService,
    private dgConfig: DocumentGenerateConfigService,
    private breakpointObserver: BreakpointObserver,
    @Inject(MAT_DIALOG_DATA) public data: {
      userLoginId: string;
      dayStart?: string;
      salesOfficeId?: string;
      showStock?: boolean;
      umpActive?: boolean;
    },
    private readonly i18n: LanguageService
  ) {}

  ngOnInit(): void {
    this.searchSubject.pipe(debounceTime(400), distinctUntilChanged()).subscribe(() => {
      this.page = 1;
      this.load();
    });
    this.shortScreenSub = this.breakpointObserver
      .observe('(max-height: 560px)')
      .subscribe((state) => (this.shortScreen = state.matches));
    this.load();
  }

  onSearchChange(value: string): void {
    this.search = value ?? '';
    this.searchSubject.next(this.search);
  }

  load(): void {
    if (!this.data?.userLoginId) {
      this.orders = [];
      this.hasNext = false;
      return;
    }
    this.loading = true;
    this.documentGenerateService
      .findMyDailyTransactions(this.data.userLoginId, this.page, this.data.dayStart, this.search)
      .subscribe({
        next: (res) => {
          const payload = res?.data ?? {};
          this.orders = payload.documents?.data ?? [];
          this.hasNext = !!payload.hasNext;
          this.loading = false;
        },
        error: () => {
          this.orders = [];
          this.hasNext = false;
          this.loading = false;
        },
      });
  }

  changePage(delta: number): void {
    const next = this.page + delta;
    if (next < 1 || (delta > 0 && !this.hasNext)) return;
    this.page = next;
    this.load();
  }

  onView(order: any): void {
    const ref = this.dialog.open(OrderDetailModalComponent, {
      data: {
        order,
        title: this.i18n.t('DOCUMENT_GENERATE.DAILY_TRANSACTIONS_MODAL.DETAIL_TITLE'),
        readonly: true,
        salesOfficeId: this.data?.salesOfficeId,
        showStock: this.data?.showStock === true,
        umpActive: this.data?.umpActive !== false,
      },
      // lleva tabla de artículos: con el tier medio quedaba angosto en pantalla amplia
      ...this.dgConfig.dialogSize('lg'),
    });
    ref.afterClosed().subscribe((result) => {
      if (result === 'print') this.onPrint(order);
    });
  }

  onPrint(order: any): void {
    if (!order?.id) return;
    this.documentGenerateService.printDocumentCopy(order.id).subscribe({
      next: (blob) => {
        const url = URL.createObjectURL(blob);
        window.open(url, '_blank');
        setTimeout(() => URL.revokeObjectURL(url), 10000);
      },
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.shortScreenSub?.unsubscribe();
    this.searchSubject.complete();
  }
}
